import { createContext, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";

// UX-level appearance preferences (TF3 settings) — text size / reduced motion / high
// contrast. Purely client-side accessibility affordances (no backend, no PII): applied
// to the document root as data-* attributes (CSS in styles.css reacts) and persisted to
// localStorage so the choice survives a reload. Language is separate (the shared i18n).
export type TextSize = "normal" | "large";
export interface Appearance {
  textSize: TextSize;
  reducedMotion: boolean;
  highContrast: boolean;
  setTextSize: (s: TextSize) => void;
  setReducedMotion: (b: boolean) => void;
  setHighContrast: (b: boolean) => void;
}

const KEY = "csa.appearance";
const AppearanceContext = createContext<Appearance | null>(null);

interface Stored {
  textSize?: TextSize;
  reducedMotion?: boolean;
  highContrast?: boolean;
}
function readStored(): Stored {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as Stored) : {};
  } catch {
    return {}; // private-mode / disabled storage → defaults, never throw
  }
}

export function SettingsProvider({ children }: { children: ReactNode }) {
  const initial = readStored();
  const [textSize, setTextSize] = useState<TextSize>(initial.textSize ?? "normal");
  const [reducedMotion, setReducedMotion] = useState<boolean>(initial.reducedMotion ?? false);
  const [highContrast, setHighContrast] = useState<boolean>(initial.highContrast ?? false);

  useEffect(() => {
    const el = document.documentElement;
    el.dataset.textSize = textSize;
    el.dataset.motion = reducedMotion ? "reduced" : "full";
    el.dataset.contrast = highContrast ? "high" : "normal";
    try {
      localStorage.setItem(KEY, JSON.stringify({ textSize, reducedMotion, highContrast }));
    } catch {
      /* storage disabled — the in-session data-* attrs still apply */
    }
  }, [textSize, reducedMotion, highContrast]);

  return (
    <AppearanceContext.Provider
      value={{
        textSize,
        reducedMotion,
        highContrast,
        setTextSize,
        setReducedMotion,
        setHighContrast,
      }}
    >
      {children}
    </AppearanceContext.Provider>
  );
}

export function useAppearance(): Appearance {
  const ctx = useContext(AppearanceContext);
  if (!ctx) throw new Error("useAppearance must be used within a SettingsProvider");
  return ctx;
}
